/**
 * =============================================================================
 * Componente: RequestPayoutModal
 * Versão: 3.0.0 (Server-side hardening — saque por programa)
 *
 * Mudanças v3.0.0:
 *   - O afiliado NÃO seleciona mais comissões individuais. Esse fluxo era
 *     vetor de exploit (cliente decidindo o que entra no saque).
 *   - Agora o afiliado escolhe APENAS o programa. O servidor agrupa todas as
 *     comissões aprovadas e disponíveis daquele programa em um único saque,
 *     com lock pessimista contra saques duplos.
 *   - O saldo exibido vem do `useAffiliateHomeOverview()` — fonte única,
 *     calculada server-side a partir do ledger (nunca de coluna agregada).
 *
 * Validação:
 *   - Front mostra saldo e mínimo apenas para UX. A decisão real (saldo
 *     suficiente, idempotência, programa ativo) é feita pela RPC
 *     `request_affiliate_payout_v2` em transação atômica.
 * =============================================================================
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2, Wallet, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { useAffiliateProfile } from '@/hooks/useAffiliateProfile';
import { useAffiliateHomeOverview } from '@/hooks/useAffiliateHomeOverview';
import { useAffiliateRequestPayout } from '@/hooks/useAffiliateRequestPayout';
import { formatCurrency } from '@/lib/currency-utils';
import type { Database } from '@/integrations/supabase/types';

type PayoutMethod = Database['public']['Enums']['affiliate_payout_method'];

interface RequestPayoutModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RequestPayoutModal({ open, onOpenChange }: RequestPayoutModalProps) {
  const { data: overview, isLoading } = useAffiliateHomeOverview();
  const { data: profile } = useAffiliateProfile();

  const [selectedProgramId, setSelectedProgramId] = useState<string | null>(null);
  const [method, setMethod] = useState<PayoutMethod>('pix');
  const [notes, setNotes] = useState<string>('');

  // Programas com saldo disponível >= mínimo (eligível para saque).
  const eligiblePrograms = useMemo(() => {
    return (overview?.programs ?? []).filter(
      (p) => p.status === 'active' && p.available > 0,
    );
  }, [overview]);

  const selectedProgram = useMemo(
    () => eligiblePrograms.find((p) => p.program_id === selectedProgramId) ?? null,
    [eligiblePrograms, selectedProgramId],
  );

  const meetsMinimum =
    !!selectedProgram && selectedProgram.available >= selectedProgram.min_payout_amount;

  // Reset ao abrir + pré-seleciona o primeiro programa elegível.
  useEffect(() => {
    if (open) {
      setMethod('pix');
      setNotes('');
      setSelectedProgramId(eligiblePrograms[0]?.program_id ?? null);
    }
  }, [open, eligiblePrograms]);

  const requestPayout = useAffiliateRequestPayout();

  function handleSubmit() {
    if (!selectedProgram) {
      toast.error('Selecione um programa.');
      return;
    }
    if (!meetsMinimum) {
      toast.error(
        `Saldo (${formatCurrency(selectedProgram.available)}) abaixo do mínimo do programa (${formatCurrency(selectedProgram.min_payout_amount)}).`,
      );
      return;
    }

    const paymentDetails: Record<string, string | null> = {};
    if (method === 'pix') {
      paymentDetails.pix_key = profile?.pix_key ?? null;
      paymentDetails.pix_key_type = profile?.pix_key_type ?? null;
    }

    requestPayout.mutate(
      {
        programId: selectedProgram.program_id,
        method,
        paymentDetails,
        notes: notes || null,
      },
      {
        onSuccess: (res) => {
          toast.success(
            `Saque de ${formatCurrency(res.amount)} solicitado com sucesso.`,
            { description: `${res.commissions_count} comissão(ões) incluídas.` },
          );
          onOpenChange(false);
        },
        onError: (err: any) => {
          // Mensagens vindas do RAISE EXCEPTION da RPC chegam em err.message.
          toast.error(err?.message ?? 'Erro ao solicitar saque.');
        },
      },
    );
  }

  const noPrograms = !isLoading && eligiblePrograms.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Solicitar saque</DialogTitle>
          <DialogDescription>
            O saldo disponível do programa selecionado será incluído integralmente
            nesta solicitação.
          </DialogDescription>
        </DialogHeader>

        {/* Loading inicial */}
        {isLoading && (
          <div className="space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        )}

        {/* Sem programas elegíveis */}
        {noPrograms && (
          <div className="flex flex-col items-center gap-2 rounded-md border border-dashed p-6 text-center">
            <Wallet className="h-6 w-6 text-muted-foreground" />
            <p className="text-sm font-medium">Nenhum saldo disponível</p>
            <p className="text-xs text-muted-foreground">
              Você precisa de comissões aprovadas em algum programa para solicitar saque.
            </p>
          </div>
        )}

        {/* Fluxo principal */}
        {!isLoading && !noPrograms && (
          <div className="space-y-4">
            {/* Seleção de programa */}
            <div className="space-y-1.5">
              <Label htmlFor="payout-program">Programa</Label>
              <Select
                value={selectedProgramId ?? undefined}
                onValueChange={(v) => setSelectedProgramId(v)}
              >
                <SelectTrigger id="payout-program">
                  <SelectValue placeholder="Selecione um programa" />
                </SelectTrigger>
                <SelectContent>
                  {eligiblePrograms.map((p) => (
                    <SelectItem key={p.program_id} value={p.program_id}>
                      {p.program_name} · {formatCurrency(p.available)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Resumo do saldo do programa selecionado */}
            {selectedProgram && (
              <div className="rounded-lg border bg-muted/40 p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    Saldo disponível
                  </span>
                  <span className="text-2xl font-bold tabular-nums">
                    {formatCurrency(selectedProgram.available)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Mínimo do programa</span>
                  <span>{formatCurrency(selectedProgram.min_payout_amount)}</span>
                </div>
                {!meetsMinimum && (
                  <Alert variant="destructive" className="mt-2">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      Saldo abaixo do mínimo deste programa.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            {/* Método de pagamento */}
            <div className="space-y-1.5">
              <Label htmlFor="payout-method">Método de pagamento</Label>
              <Select
                value={method}
                onValueChange={(v) => setMethod(v as PayoutMethod)}
              >
                <SelectTrigger id="payout-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pix">PIX</SelectItem>
                  <SelectItem value="bank_transfer">Transferência bancária</SelectItem>
                  <SelectItem value="manual">Manual / outros</SelectItem>
                </SelectContent>
              </Select>
              {method === 'pix' && !profile?.pix_key && (
                <p className="text-xs text-amber-600">
                  Cadastre sua chave PIX no perfil para agilizar o pagamento.
                </p>
              )}
            </div>

            {/* Observação */}
            <div className="space-y-1.5">
              <Label htmlFor="payout-notes">Observação (opcional)</Label>
              <Textarea
                id="payout-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Alguma informação adicional para o processamento?"
                rows={2}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={requestPayout.isPending}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              noPrograms ||
              isLoading ||
              !selectedProgram ||
              !meetsMinimum ||
              requestPayout.isPending
            }
          >
            {requestPayout.isPending && (
              <Loader2 className="h-4 w-4 animate-spin" />
            )}
            Solicitar{' '}
            {selectedProgram && meetsMinimum
              ? formatCurrency(selectedProgram.available)
              : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
